import { useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMediaSettingsStore, usePeersStore } from '@src/stores';
import { isWebView } from '@src/helpers';

import { IAudioStream } from '@src/types';

const useAudioStream = (audioStream: IAudioStream) => {
  const [isMuted, setIsMuted] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const currentDeviceRef = useRef<string>('');

  const { selectedSpeakerDevice, speakerDevices } = useMediaSettingsStore(
    useShallow((state) => ({
      selectedSpeakerDevice: state.mediaSettings.selectedSpeakerDevice,
      speakerDevices: state.mediaSettings.speakerDevices,
    }))
  );

  const { peers } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
    }))
  );

  const { stream, volume, peerId, isCurrentUser } = audioStream;

  const currentStreamPeer = useMemo(() => peers.get(peerId), [peerId, peers]);

  const speakerDeviceId = useMemo(() => selectedSpeakerDevice?.deviceId, [selectedSpeakerDevice?.deviceId]);

  // Check if a device is still available
  const isDeviceAvailable = (deviceId: string) => {
    if (!deviceId || deviceId === '' || deviceId === 'default') return true;
    return speakerDevices.some((device) => device.deviceId === deviceId);
  };

  useEffect(() => {
    if (!currentStreamPeer?.peer_info) {
      return;
    }
    const currentStreamPeerAudio = !!currentStreamPeer.peer_info.peer_audio;
    setIsMuted(!!isCurrentUser || !currentStreamPeerAudio);
  }, [currentStreamPeer, isCurrentUser, peerId, peers]);

  useEffect(() => {
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // On Android *browser* (not generic mobile / webviews), rely entirely on OS routing
    // for audio output. This matches the behavior of sites like YouTube, which don't pin
    // a specific output device and therefore automatically follow Bluetooth reconnects.
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isAndroidBrowser = isAndroid && !isWebView();

    if (isAndroidBrowser) {
      if (audioRef.current) {
        audioRef.current.srcObject = stream;
        audioRef.current.volume = volume;
        audioRef.current.autoplay = true;
      }
      // Skip manual setSinkId logic on mobile so the OS can manage routing (e.g. Bluetooth).
      return;
    }

    const MAX_RETRY_ATTEMPTS = 5; // Reduced from 10 since most issues are not transient

    const attachSinkId = async (attemptCount = 0) => {
      if (!isMountedRef.current || !audioRef.current || !audioRef.current.setSinkId) {
        return;
      }

      // Stop retrying after maximum attempts
      if (attemptCount >= MAX_RETRY_ATTEMPTS) {
        console.warn(`❌ Stopped retrying audio output device after ${MAX_RETRY_ATTEMPTS} attempts`);
        // Force fallback to default device
        try {
          await audioRef.current.setSinkId('');
          console.log('✅ Final fallback to default audio output device successful');
          currentDeviceRef.current = '';
        } catch (error) {
          console.error('❌ Failed to fallback to default device:', error);
        }
        return;
      }

      // Determine which device to try
      let deviceToTry = '';

      // Strategy: Always try default device first when there are issues
      if (attemptCount === 0) {
        // First attempt: try the selected device if it exists and looks valid
        if (speakerDeviceId && isDeviceAvailable(speakerDeviceId)) {
          deviceToTry = speakerDeviceId;
        } else {
          deviceToTry = ''; // Default device
        }
      } else {
        // After first failure, always prefer default device
        deviceToTry = '';

        // If default device also fails, try other available devices
        if (attemptCount >= 2) {
          const availableDevices = speakerDevices
            .filter((d) => d.deviceId && d.deviceId !== speakerDeviceId)
            .map((d) => d.deviceId);

          if (availableDevices.length > 0) {
            const deviceIndex = (attemptCount - 2) % availableDevices.length;
            deviceToTry = availableDevices[deviceIndex];
          }
        }
      }

      try {
        // Skip if we're already using this device
        if (currentDeviceRef.current === deviceToTry) {
          return;
        }

        // Add a delay before attempting setSinkId to handle race conditions
        if (attemptCount > 0) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Verify device is still available right before setting it
        if (deviceToTry && deviceToTry !== '' && deviceToTry !== 'default') {
          const currentDevices = await navigator.mediaDevices.enumerateDevices();
          const currentSpeakers = currentDevices.filter((d) => d.kind === 'audiooutput');
          const deviceExists = currentSpeakers.some((d) => d.deviceId === deviceToTry);

          if (!deviceExists) {
            console.warn(`🔄 Device ${deviceToTry} no longer exists, falling back to default`);
            deviceToTry = '';
          }
        }

        await audioRef.current.setSinkId(deviceToTry);
        console.log(
          `✅ Successfully set audio output device: ${deviceToTry || 'default'} (attempt ${attemptCount + 1})`
        );
        currentDeviceRef.current = deviceToTry;
        return;
      } catch (error: any) {
        // Handle AbortError specifically
        if (error.name === 'AbortError') {
          console.warn(
            `⚠️ Audio device operation aborted (attempt ${attemptCount + 1}). Device: ${deviceToTry || 'default'}`
          );

          // For AbortError, immediately try default if we weren't already trying it
          if (deviceToTry !== '') {
            console.log('🔄 Trying default device immediately due to AbortError...');
            try {
              await audioRef.current.setSinkId('');
              console.log('✅ Successfully set default audio output device after AbortError');
              currentDeviceRef.current = '';
              return;
            } catch (defaultError) {
              console.warn('⚠️ Default device also failed after AbortError:', defaultError);
            }
          }
        } else if (error.name === 'NotAllowedError') {
          console.warn(
            `🔒 Permission denied for audio device (attempt ${attemptCount + 1}). Device: ${deviceToTry || 'default'}`
          );
          // Don't retry permission errors
          return;
        } else if (error.name === 'NotFoundError') {
          console.warn(`❌ Audio device not found (attempt ${attemptCount + 1}). Device: ${deviceToTry || 'default'}`);
          // Device definitely doesn't exist, try default immediately
          if (deviceToTry !== '') {
            try {
              await audioRef.current.setSinkId('');
              console.log('✅ Fallback to default device after NotFoundError');
              currentDeviceRef.current = '';
              return;
            } catch (defaultError) {
              console.warn('⚠️ Default device failed after NotFoundError:', defaultError);
            }
          }
        }

        console.warn(
          `⚠️ Failed to set audio output device "${deviceToTry || 'default'}" (attempt ${attemptCount + 1}):`,
          error.name,
          error.message
        );

        // Schedule retry with longer backoff to give system time to stabilize
        if (isMountedRef.current && attemptCount < MAX_RETRY_ATTEMPTS - 1) {
          const retryDelay = attemptCount === 0 ? 500 : 1500; // Longer delays: 500ms first retry, 1.5s after
          console.log(`🔄 Retrying in ${retryDelay}ms... (${attemptCount + 2}/${MAX_RETRY_ATTEMPTS})`);
          retryTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              attachSinkId(attemptCount + 1);
            }
          }, retryDelay);
        }
      }
    };

    // Additional check to refresh device list before attempting connection
    const attemptConnection = async () => {
      try {
        // Refresh device list to ensure we have current devices
        await navigator.mediaDevices.enumerateDevices();

        if (audioRef.current) {
          audioRef.current.srcObject = stream;
          audioRef.current.volume = volume;
          audioRef.current.autoplay = true;

          // Start the connection attempt
          attachSinkId(0);
        }
      } catch (error) {
        console.error('Failed to refresh devices before audio connection:', error);
        // Still try to connect with current device list
        if (audioRef.current) {
          audioRef.current.srcObject = stream;
          audioRef.current.volume = volume;
          audioRef.current.autoplay = true;
          attachSinkId(0);
        }
      }
    };

    attemptConnection();
  }, [stream, volume, speakerDeviceId, speakerDevices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    isMuted,
    audioRef,
  };
};

export default useAudioStream;
